import { Fit, Layout, Rive } from '@rive-app/canvas-lite'
import { useQuery, useQueryClient } from '@tanstack/solid-query'
import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from 'solid-js'
import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import rewardRiv from '~/assets/rive/reward.riv'
import { animateCount } from '~/lib/animate-count'
import type { RevealContent } from '~/lib/delivery'
import { type Flyer, planFlyers } from '~/lib/flying-currency'
import { getLocalizedMessage } from '~/lib/l10n'
import { type CurrencyDelta, revealStatus } from '~/lib/reward-reveal'
import type { RewardVisual } from '~/lib/reward-visuals'
import { m } from '~/paraglide/messages'
import { type UserCurrency, userQueries } from '~/services/user'
import { Spinner } from './spinner'

const FLYER_FLIGHT_MS = 620

// A single coin/gem flying from the revealed reward up to its balance pill.
// A double rAF flip drives the CSS transform to the pill — one frame paints
// the start state so the transition actually runs (a single rAF can commit
// both states in one frame, and the coin jumps instead of flying). It fades
// and shrinks in flight; the parent sweeps it once the burst has landed.
function FlyerItem(props: { flyer: Flyer }) {
  const [launched, setLaunched] = createSignal(false)
  onMount(() =>
    requestAnimationFrame(() => requestAnimationFrame(() => setLaunched(true))),
  )
  const transform = () =>
    launched()
      ? `translate(${props.flyer.endX - props.flyer.startX}px, ${props.flyer.endY - props.flyer.startY}px) scale(0.5)`
      : 'translate(0, 0) scale(1)'
  return (
    <img
      src={props.flyer.kind === 'gem' ? gemIcon : coinIcon}
      alt=""
      aria-hidden="true"
      draggable={false}
      class="pointer-events-none fixed z-[70] size-6 will-change-transform"
      style={{
        left: `${props.flyer.startX - 12}px`,
        top: `${props.flyer.startY - 12}px`,
        'transition-property': 'transform, opacity',
        'transition-duration': `${FLYER_FLIGHT_MS}ms`,
        'transition-timing-function': 'cubic-bezier(0.55, 0, 0.1, 1)',
        'transition-delay': `${props.flyer.delayMs}ms`,
        transform: transform(),
        opacity: launched() ? 0.3 : 1,
      }}
    />
  )
}

const REVEAL_STATE_MACHINE = 'State Machine 1'

// Figma reveal background: every reward type shares the green radial (gems
// used to have a yellow variant of set 369:1963088; design dropped it). The
// gradient sits on its own 100% stop as a solid base — it has a 0.95-alpha
// mid stop, and without an opaque base the page would ghost through.
const GREEN_BG = {
  'background-image':
    'radial-gradient(circle at 50% 45%, #FFFEB7 6%, rgba(116, 200, 171, 0.95) 58%, #51A387 100%)',
  'background-color': '#51A387',
}

// The pill's 9999+ cap is display-only; the cached balance stays exact.
// Plain digits, no separators — the cap keeps values to 4 digits (per Figma).
function pillDisplay(value: number): string {
  return value > 9999 ? '9999+' : String(value)
}

function CurrencyPill(props: {
  icon: string
  value: number | undefined
  ref?: (el: HTMLDivElement) => void
}) {
  return (
    <div
      ref={props.ref}
      class="flex h-9 origin-center items-center gap-1 rounded-[20px] bg-black/20 px-3 text-white opacity-80 will-change-transform"
    >
      <img src={props.icon} alt="" class="size-5" />
      <Show when={props.value != null} fallback={<Spinner class="size-4" />}>
        <span class="text-sm font-semibold tabular-nums leading-5">
          {pillDisplay(props.value!)}
        </span>
      </Show>
    </div>
  )
}

/**
 * The reward reveal (see CONTEXT.md): the full-screen celebration shown after
 * a milestone claim or the bind reward was already delivered. It presents the
 * reward delivery, not the nominal mapping; its Claim button only credits the
 * local balance mirror and dismisses — it never requests a second delivery.
 */
export function RewardReveal(props: {
  content: RevealContent
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  // The claim/redeem mutations block on warming this query before the
  // delivery request (a failed fetch fails the claim), so by the time the
  // reveal mounts the cached pre-claim snapshot is always present — and
  // never refetched (staleTime), which would return the already-credited
  // balance.
  const balance = useQuery(() => userQueries.currency())

  const visual = () => props.content.visual
  const delta = () => props.content.delta
  const background = () => GREEN_BG

  // Server-derived content carries a backend title key the native l10n table
  // resolves; until (or unless) it does, the client-known name shows.
  const [resolvedName, setResolvedName] = createSignal<string | null>(null)
  onMount(() => {
    const key = props.content.titleKey
    if (!key) return
    getLocalizedMessage(key)
      .then((name) => name && setResolvedName(name))
      .catch(() => {})
  })
  const title = (v: RewardVisual): string => {
    switch (v.kind) {
      case 'coins':
        return `${m.coin()} x${v.amount}`
      case 'gems':
        return `${m.time_crystal()} x${v.amount}`
      case 'content':
        return resolvedName() ?? v.name
    }
  }

  let canvas: HTMLCanvasElement | undefined
  let rive: Rive | undefined
  let rewardEl: HTMLDivElement | undefined
  let coinPillEl: HTMLDivElement | undefined
  let gemPillEl: HTMLDivElement | undefined

  // Coins/gems that fly from the revealed reward up to the balance pills.
  const [flyers, setFlyers] = createSignal<Flyer[]>([])
  const timers: number[] = []
  // Bump a pill via the Web Animations API — no dependency on a generated
  // Tailwind class or classList reactivity, so it fires reliably. Keyframes
  // match birthday-campaign's `stampPillPop` (plain scale 1 -> 1.2 -> 1). One
  // pop fires per landing coin, so a staggered burst makes the pill jitter
  // repeatedly; cancelling the previous pop first restarts it cleanly from
  // scale 1, mirroring birthday-campaign's remove-then-re-add of the class.
  const popAnims: Partial<Record<Flyer['kind'], Animation>> = {}
  const popPill = (kind: Flyer['kind'], el: HTMLDivElement | undefined) => {
    popAnims[kind]?.cancel()
    popAnims[kind] =
      el?.animate?.(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.2)', offset: 0.4 },
          { transform: 'scale(1)' },
        ],
        { duration: 320, easing: 'ease-out' },
      ) ?? undefined
  }
  // Pop as each coin visually meets the pill — just before the flight ends.
  const POP_ARRIVAL_MS = Math.round(FLYER_FLIGHT_MS * 0.85)
  /** Returns how long the burst runs (ms), so claim() can close after it. */
  const launchFlyers = (d: CurrencyDelta): number => {
    if (!rewardEl) return 0
    const r = rewardEl.getBoundingClientRect()
    const toRect = (el: HTMLDivElement | undefined) => {
      if (!el) return undefined
      const b = el.getBoundingClientRect()
      return { left: b.left, top: b.top, width: b.width, height: b.height }
    }
    const planned = planFlyers({
      source: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
      coinTarget: toRect(coinPillEl),
      gemTarget: toRect(gemPillEl),
      delta: d,
    })
    if (planned.length === 0) return 0
    setFlyers(planned)
    // One pop per landing coin/gem — the pill bounces repeatedly as the fan
    // arrives (birthday-campaign's reward flow).
    for (const f of planned) {
      const el = f.kind === 'coin' ? coinPillEl : gemPillEl
      timers.push(
        window.setTimeout(
          () => popPill(f.kind, el),
          f.delayMs + POP_ARRIVAL_MS,
        ),
      )
    }
    // Clear the burst once the last flyer has landed. A timed sweep (not
    // per-coin transitionend) is deterministic: a transition that never
    // starts fires no transitionend, which would otherwise strand the coins.
    const maxDelay = planned.reduce((m, f) => Math.max(m, f.delayMs), 0)
    const totalMs = maxDelay + FLYER_FLIGHT_MS + 120
    timers.push(window.setTimeout(() => setFlyers([]), totalMs))
    return totalMs
  }
  const cancels: (() => void)[] = []
  onCleanup(() => {
    for (const t of timers) clearTimeout(t)
    for (const cancel of cancels) cancel()
  })

  onMount(() => {
    if (!canvas) return
    const status = revealStatus(visual())
    rive = new Rive({
      canvas,
      src: rewardRiv,
      artboard: 'reward_reveal',
      stateMachines: [REVEAL_STATE_MACHINE],
      autoplay: true,
      shouldDisableRiveListeners: true,
      layout: new Layout({ fit: Fit.FitHeight }),
      onLoad: () => {
        rive?.resizeDrawingSurfaceToCanvas()
        for (const input of rive?.stateMachineInputs(REVEAL_STATE_MACHINE) ??
          []) {
          if (input.name === 'status') input.value = status
          // `repeat` drives focus-pass's duplicate-item fallback; unused here.
          if (input.name === 'repeat') input.value = false
        }
      },
    })
    const onResize = () => rive?.resizeDrawingSurfaceToCanvas()
    window.addEventListener('resize', onResize)
    onCleanup(() => {
      window.removeEventListener('resize', onResize)
      rive?.cleanup()
    })
  })

  // Pills mirror focus-pass: seed from the balance query. The count-up and
  // coin flight wait for the Claim tap (see claim()); the real cache credit
  // still happens once, on close.
  const [displayed, setDisplayed] = createSignal<UserCurrency | null>(null)
  let counted = false
  createEffect(() => {
    const data = balance.data
    if (!data || counted) return
    counted = true
    setDisplayed({ ...data })
  })

  const [buttonVisible, setButtonVisible] = createSignal(false)
  onMount(() => {
    const timer = window.setTimeout(() => setButtonVisible(true), 500)
    onCleanup(() => clearTimeout(timer))
  })

  let closing = false
  const close = () => {
    if (closing) return
    closing = true
    const d = delta()
    // No cached balance → skip the credit: a balance fetched later already
    // includes this delivery server-side, so crediting it would double-count.
    queryClient.setQueryData(
      userQueries.currency().queryKey,
      (prev: UserCurrency | undefined) =>
        prev ? { coin: prev.coin + d.coin, gem: prev.gem + d.gem } : prev,
    )
    props.onClose()
  }

  const COUNT_UP_MS = 1000
  // Claim plays the celebration — coin flight plus count-up — then dismisses
  // on its own once the last coin has landed and the numbers have settled.
  // A currency-less reward (tree/land skin) has nothing to animate, so it
  // just closes; the no-balance arm is defensive (the snapshot is warmed
  // before the reveal can open).
  const [claiming, setClaiming] = createSignal(false)
  const claim = () => {
    if (claiming()) return
    setClaiming(true)
    const data = balance.data
    const d = delta()
    if (!data || (d.coin === 0 && d.gem === 0)) {
      close()
      return
    }
    const flightMs = launchFlyers(d)
    cancels.push(
      animateCount({
        from: data.coin,
        delta: d.coin,
        duration: COUNT_UP_MS,
        onTick: (value) =>
          setDisplayed((prev) => prev && { ...prev, coin: value }),
      }),
      animateCount({
        from: data.gem,
        delta: d.gem,
        duration: COUNT_UP_MS,
        onTick: (value) =>
          setDisplayed((prev) => prev && { ...prev, gem: value }),
      }),
    )
    timers.push(window.setTimeout(close, Math.max(flightMs, COUNT_UP_MS) + 250))
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      // pointer-events-auto: the bind-reward flow opens this while the
      // enter-code drawer is still open, and corvu's modal lock sets
      // pointer-events:none on <body>, re-enabling only its own portals —
      // this portal isn't corvu-managed, so it must opt back in itself.
      class="pointer-events-auto fixed inset-0 z-[60] flex min-w-[320px] touch-none flex-col duration-300 ease-out animate-in fade-in"
      style={background()}
    >
      {/* balance pills */}
      <div class="flex justify-end gap-1 px-4 pt-[calc(var(--safe-area-top)+16px)]">
        <CurrencyPill
          icon={coinIcon}
          value={displayed()?.coin}
          ref={(el) => {
            coinPillEl = el
          }}
        />
        <CurrencyPill
          icon={gemIcon}
          value={displayed()?.gem}
          ref={(el) => {
            gemPillEl = el
          }}
        />
      </div>

      {/* main */}
      <div class="flex flex-1 flex-col justify-center gap-6 px-6">
        <h1 class="whitespace-pre-line text-center text-xl font-semibold leading-7 text-white">
          {m.claimed_title()}
        </h1>

        <div
          ref={(el) => {
            rewardEl = el
          }}
          class="relative mx-auto flex h-[40vh] w-full max-w-[40vh] items-center justify-center"
        >
          <canvas
            ref={(el) => {
              canvas = el
            }}
            class="size-full"
          />
          <Show when={props.content.imageUrl}>
            {(src) => (
              <img
                src={src()}
                alt=""
                class="absolute inset-0 m-auto size-1/2 object-contain duration-500 ease-out animate-in fade-in zoom-in-75"
              />
            )}
          </Show>
        </div>

        <Show when={title(visual())}>
          <h2 class="text-center text-[26px] font-bold leading-9 text-white [text-shadow:0_0_15px_rgba(255,255,255,0.7)]">
            {title(visual())}
          </h2>
        </Show>
      </div>

      {/* footer CTA */}
      <div class="shrink-0 bg-gradient-to-b from-[rgba(9,21,36,0)] from-[3%] to-[rgba(9,21,36,0.2)] px-6 pb-12 pt-4 backdrop-blur-[18px]">
        <div class="mx-auto h-[52px] w-full max-w-[640px]">
          <Show when={buttonVisible()}>
            <button
              type="button"
              disabled={claiming()}
              class="flex h-[52px] w-full items-center justify-center rounded-full bg-white text-base font-bold leading-5 text-cta shadow-[0_0_20px_6px_rgba(198,255,205,0.14)] transition-opacity duration-300 ease-out animate-in fade-in active:bg-tertiary-press disabled:opacity-30"
              onClick={claim}
            >
              {m.claim()}
            </button>
          </Show>
        </div>
      </div>

      {/* coins/gems flying up to the balance pills */}
      <For each={flyers()}>{(flyer) => <FlyerItem flyer={flyer} />}</For>
    </div>
  )
}
